import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, getCountFromServer, query, where, getDocs, orderBy, limit, collectionGroup } from 'firebase/firestore';
import { db } from '../../firebase'; // Fixed: Use correct firebase import path
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, FileText, DollarSign, Calendar, TrendingUp, Activity, AlertCircle, Receipt, Building2, Download, Megaphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // ADDED: Import useNavigate
import { cn } from '../../lib/utils';
import { useTheme } from '../ui/theme-provider'; // Add this import

// Simple loading placeholder component to replace Skeleton
const LoadingPlaceholder = ({ className = "h-6 w-16" }) => (
  <div className={`bg-muted animate-pulse rounded ${className}`}></div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInvoices: 0,
    monthlyInvoices: 0,
    totalExpenses: 0,
    monthlyExpenses: 0,
    activeUsers: 0,
    averageInvoicesPerUser: 0
  });
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const { toast } = useToast();
  const navigate = useNavigate(); // ADDED: Add navigate hook
  const { theme } = useTheme(); // Add this hook

  // Authorization check
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          setIsAuthorized(!!idTokenResult.claims.admin);
        } catch (error) {
          console.error("Error fetching admin status:", error);
          setIsAuthorized(false);
        }
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  // Fetch dashboard data
  useEffect(() => {
    if (!isAuthorized) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // 1. Get total user count (1 read)
        const usersQuery = query(collection(db, 'users'));
        const userCountSnapshot = await getCountFromServer(usersQuery);
        const totalUsers = userCountSnapshot.data().count;

        // 2. Get total invoice count across ALL users using collectionGroup (1 read)
        const allInvoicesQuery = query(collectionGroup(db, 'invoices'));
        const totalInvoiceSnapshot = await getCountFromServer(allInvoicesQuery);
        const totalInvoices = totalInvoiceSnapshot.data().count;

        // 3. Get this month's invoice count (1 read)
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthlyInvoicesQuery = query(
          collectionGroup(db, 'invoices'),
          where('createdAt', '>=', startOfMonth)
        );
        const monthlyInvoiceSnapshot = await getCountFromServer(monthlyInvoicesQuery);
        const monthlyInvoices = monthlyInvoiceSnapshot.data().count;

        // 4. Get total expense count across ALL users using collectionGroup (1 read)
        const allExpensesQuery = query(collectionGroup(db, 'expenses'));
        const totalExpenseSnapshot = await getCountFromServer(allExpensesQuery);
        const totalExpenses = totalExpenseSnapshot.data().count;

        // 5. Get this month's expense count (1 read)
        const monthlyExpensesQuery = query(
          collectionGroup(db, 'expenses'),
          where('date', '>=', startOfMonth)
        );
        const monthlyExpenseSnapshot = await getCountFromServer(monthlyExpensesQuery);
        const monthlyExpenses = monthlyExpenseSnapshot.data().count;

        // Calculate simple stats
        const averageInvoicesPerUser = totalUsers > 0 ? (totalInvoices / totalUsers).toFixed(1) : 0;

        setStats({
          totalUsers,
          totalInvoices,
          monthlyInvoices,
          totalExpenses,
          monthlyExpenses,
          activeUsers: 0, // We'd need to read actual user activity data for this
          averageInvoicesPerUser
        });

        toast({
          title: "Dashboard Loaded",
          description: `${totalUsers} users, ${totalInvoices} invoices, ${totalExpenses} expenses.`,
          variant: "success"
        });

        // **Total cost: Only 5 reads!**

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthorized, toast]);

  if (!isAuthorized && !loading) { // Added !loading to prevent flash of access denied
    return (
      <div className="container mx-auto p-4">
        <Card>
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p>You don't have permission to view the admin dashboard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount) => `£${(amount || 0).toFixed(2)}`;

  const StatCard = ({ title, value, icon: Icon, description, trend, colorScheme = "text-muted-foreground" }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${colorScheme}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {loading ? <LoadingPlaceholder className="h-6 w-16" /> : value}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend && (
          <Badge variant="secondary" className="mt-2 text-xs">
            {trend}
          </Badge>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex items-center gap-2">
          <Badge variant="default" className="bg-purple-600 hover:bg-purple-700 text-white">5 Firebase Reads</Badge>
          {loading && <AlertCircle className="h-4 w-4 animate-spin" />}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={Users}
          description="Registered accounts"
          colorScheme="text-blue-500"
        />
        
        <StatCard
          title="Total Invoices"
          value={stats.totalInvoices}
          icon={FileText}
          description="All time"
          colorScheme="text-green-500"
        />
        
        <StatCard
          title="Monthly Invoices"
          value={stats.monthlyInvoices}
          icon={Calendar}
          description="This month"
          colorScheme="text-yellow-500"
        />
        
        <StatCard
          title="Total Expenses"
          value={stats.totalExpenses}
          icon={Receipt}
          description="All time"
          colorScheme="text-red-500"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard
          title="Monthly Expenses"
          value={stats.monthlyExpenses}
          icon={TrendingUp}
          description="This month"
          colorScheme="text-pink-500"
        />
        
        <StatCard
          title="Avg Invoices per User"
          value={stats.averageInvoicesPerUser}
          icon={Activity}
          description="Engagement metric"
          colorScheme="text-indigo-500"
        />
      </div>

      {/* Usage Insights */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Usage Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Invoice/Expense Ratio</span>
              <Badge variant="outline" className="border-blue-500 text-blue-500">
                {stats.totalExpenses > 0 ? (stats.totalInvoices / stats.totalExpenses).toFixed(1) : 'N/A'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Monthly Activity</span>
              <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                {stats.monthlyInvoices + stats.monthlyExpenses} transactions
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">User Adoption</span>
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                {stats.totalUsers > 0 && stats.totalInvoices > 0 ? 
                  Math.round((stats.totalInvoices / stats.totalUsers) * 100) / 100 : 0} invoices/user
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">System Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Read Efficiency</span>
              <Badge variant="default" className="bg-purple-600 hover:bg-purple-700 text-white">5 reads only</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Data Strategy</span>
              <Badge variant="outline" className="border-teal-500 text-teal-500">CollectionGroup Counts</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Cache Strategy</span>
              <Badge variant="outline" className="border-gray-500 text-gray-500">Real-time</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Practice Verification Card - New Addition */}
      <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/practice-verification')}>
        <CardContent className="p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Practice Verifications</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Review practice management claims</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RSS Job Manager Card */}
      <Card className={cn(
            "cursor-pointer transition-all hover:shadow-lg",
            theme === 'light' ? 'bg-white border-gray-200 hover:border-purple-300' : 'bg-[#2A2A3E] border-purple-500/20 hover:border-purple-500/40'
          )}
          onClick={() => navigate('/admin/rss-jobs')}
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'light' ? 'bg-purple-100' : 'bg-purple-900/30'
                )}>
                  <Download className={cn(
                    "h-6 w-6",
                    theme === 'light' ? 'text-purple-600' : 'text-purple-400'
                  )} />
                </div>
                <div>
                  <h3 className={cn(
                    "text-lg font-semibold",
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    RSS Job Manager
                  </h3>
                  <p className={cn(
                    "text-sm",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    Fetch external job listings
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Blog Management Card */}
          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-lg",
            theme === 'light' ? 'bg-white border-gray-200 hover:border-purple-300' : 'bg-[#2A2A3E] border-purple-500/20 hover:border-purple-500/40'
          )}
          onClick={() => navigate('/admin/blog')}
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'light' ? 'bg-blue-100' : 'bg-blue-900/30'
                )}>
                  <FileText className={cn(
                    "h-6 w-6",
                    theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                  )} />
                </div>
                <div>
                  <h3 className={cn(
                    "text-lg font-semibold",
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    Blog Management
                  </h3>
                  <p className={cn(
                    "text-sm",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    Create and manage blog content
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media Management Card */}
          <Card className={cn(
            "cursor-pointer transition-all hover:shadow-lg",
            theme === 'light' ? 'bg-white border-gray-200 hover:border-purple-300' : 'bg-[#2A2A3E] border-purple-500/20 hover:border-purple-500/40'
          )}
          onClick={() => navigate('/admin/social-media')}
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'light' ? 'bg-pink-100' : 'bg-pink-900/30'
                )}>
                  <Megaphone className={cn(
                    "h-6 w-6",
                    theme === 'light' ? 'text-pink-600' : 'text-pink-400'
                  )} />
                </div>
                <div>
                  <h3 className={cn(
                    "text-lg font-semibold",
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    Social Media Management
                  </h3>
                  <p className={cn(
                    "text-sm",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    Create social media posts from jobs and blogs
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

      {loading && (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4 animate-spin" />
              <span>Loading dashboard data...</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Counting documents efficiently using collectionGroup queries.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              If data appears incorrect or remains at 0, please check the browser console for Firestore indexing errors and create any required indexes in your Firebase project.
            </p>
          </CardContent>
        </Card>
      )}
      {!loading && stats.totalUsers === 0 && stats.totalInvoices === 0 && (
         <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              <span>No data or potential issue</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Dashboard data is currently showing zeros. This could be due to no data or a Firestore indexing issue.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Please check the browser console for any error messages, especially regarding Firestore indexes. Follow the links provided in console errors to create necessary indexes in your Firebase project. Indexes can take a few minutes to build.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
